/**
 * @file src/components/Footer.jsx
 * @description Page footer with company info, links, and legal documents
 * 
 * Displays at bottom of all pages with:
 * - LegaxyVault logo
 * - Quick links (About, Pricing, Blog)
 * - Legal links (Privacy Policy, Terms of Service)
 * - Social media links
 * - Copyright notice
 * 
 * @component
 * @returns {React.ReactElement}
 * 
 * @example
 *   // In App.jsx layout
 *   <Header />
 *   <main>{children}</main>
 *   <Footer />
 */

import React, { lazy, Suspense } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logoSrc from '../assets/VL_Logo.png';
const NewsletterInline = lazy(() => import('./NewsletterInline'));

const FOOTER_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/capsuleList', label: 'My Capsules' },
  { to: '/blog', label: 'Blog' },
  { to: '/contact', label: 'Contact' },
  { to: '/privacy', label: 'Privacy Policy' },
  { to: '/tos', label: 'Terms of Service' },
];

const Footer = () => {
  const { pathname } = useLocation();
  return (
    <footer className="bg-vault-900 border-t border-vault-800" role="contentinfo">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src={logoSrc} alt="LegaxyVault" className="h-8 w-auto object-contain" />
            <span className="text-sm font-semibold text-white">LegaxyVault</span>
          </div>
          <a
            href="https://www.producthunt.com/products/legaxyvault?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-legaxyvault"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:block"
          >
            <img
              src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1129411&theme=light&t=1776999323401"
              alt="LegaxyVault | Product Hunt"
              width="150"
              height="32"
              loading="lazy"
            />
          </a>
          <nav aria-label="Footer navigation" className="flex items-center gap-6">
            {FOOTER_LINKS.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                aria-current={pathname === to ? 'page' : undefined}
                className="text-vault-400 hover:text-white text-sm transition-colors"
              >
                {label}
              </Link>
            ))}
          </nav>
        </div>
        <div className="mt-8 pt-6 border-t border-vault-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <p className="text-vault-400 text-xs whitespace-nowrap">Get legacy tips in your inbox:</p>
            <Suspense fallback={null}>
              <NewsletterInline variant="dark" source="footer" />
            </Suspense>
          </div>
          <p className="text-vault-500 text-xs">
            &copy; {new Date().getFullYear()} LegaxyVault. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
